import React, { useEffect, useRef, useState } from "react";
import { Mic, Lock, ChevronLeft, ChevronUp, SendHorizontal, Trash2 } from "lucide-react";
import useGrabadorAudio from "../../../hooks/useGrabadorAudio";
import "./LAEAI.css";

/**
 * Botón de audio de LAE-AI (estilo mensajería):
 * - Mantener pulsado: graba; al soltar, envía.
 * - Mientras se mantiene: deslizar a la izquierda cancela; deslizar hacia arriba bloquea (graba sin mantener).
 * - Clic corto: graba en modo bloqueado. En bloqueado: botones Cancelar y Enviar (o Escape para cancelar).
 * - Al llegar al máximo de segundos se envía solo.
 * - Al cancelar, el micrófono cae en una papelera (animación corta; sin ella si el sistema pide reducir movimiento).
 * onAudio(wavBlob) recibe el audio listo para transcribir; onError(codigo) los errores de micrófono.
 */
const MAX_SEGUNDOS = 60;
const UMBRAL_CANCELAR = 80; // px a la izquierda
const UMBRAL_BLOQUEAR = 60; // px hacia arriba
const CLIC_MS = 300;
const PAPELERA_MS = 1100; // duración de la animación de descartar (igual que en LAEAI.css)

/** Papelera con la tapa aparte, para poder abrirla (mismo trazo que el Trash2 de lucide) */
const Papelera = () => (
  <svg className="laeai-papelera__cubo" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <g className="laeai-papelera__tapa">
      <path d="M3 6h18" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </g>
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const formatoTiempo = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function LAEAIGrabador({ deshabilitado, onAudio, onError, t }) {
  // null | "mantener" | "bloqueado"
  const [modo, setModo] = useState(null);
  const [desplazamiento, setDesplazamiento] = useState({ x: 0, y: 0 });
  // Animación de "a la papelera" tras cancelar
  const [tirando, setTirando] = useState(false);
  const temporizadorPapelera = useRef(null);
  useEffect(() => () => clearTimeout(temporizadorPapelera.current), []);
  const gesto = useRef(null);
  const modoRef = useRef(null);
  modoRef.current = modo;

  const enviarRef = useRef(null);
  const { soportado, grabando, segundos, preparar, iniciar, detener, cancelar } = useGrabadorAudio({
    maxSegundos: MAX_SEGUNDOS,
    onLimite: () => enviarRef.current?.(),
  });

  const terminar = () => {
    setModo(null);
    setDesplazamiento({ x: 0, y: 0 });
    gesto.current = null;
  };

  const enviar = async () => {
    terminar();
    try {
      const wav = await detener();
      // Sin voz (silencio o ruido): no se envía a transcribir
      if (wav) onAudio(wav);
      else onError?.("SIN_VOZ");
    } catch {
      onError?.("AUDIO_FORMATO_INVALIDO");
    }
  };
  enviarRef.current = enviar;

  const descartar = () => {
    // Solo si ya estaba grabando: si aún se preparaba el micrófono no hay audio que tirar
    const habiaAudio = grabando;
    terminar();
    cancelar();
    if (!habiaAudio) return;
    clearTimeout(temporizadorPapelera.current);
    setTirando(true);
    temporizadorPapelera.current = setTimeout(() => setTirando(false), PAPELERA_MS);
  };

  // Escape cancela la grabación
  useEffect(() => {
    if (!modo) return undefined;
    const alPulsar = (e) => { if (e.key === "Escape") descartar(); };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  const alPresionar = async (e) => {
    if (deshabilitado || modo) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesto.current = { x: e.clientX, y: e.clientY, t: Date.now(), soltado: false };
    setModo("mantener");
    try {
      await iniciar();
    } catch (error) {
      terminar();
      onError?.(error.codigo || "MIC_NO_DISPONIBLE");
      return;
    }
    // Canceló mientras se pedía permiso al micrófono: no dejar una grabación huérfana
    if (!modoRef.current) {
      cancelar();
      return;
    }
    // Si soltó mientras se pedía permiso al micrófono, queda grabando en modo bloqueado
    if (gesto.current?.soltado && modoRef.current === "mantener") setModo("bloqueado");
  };

  const alMover = (e) => {
    if (modoRef.current !== "mantener" || !gesto.current) return;
    const x = Math.min(0, e.clientX - gesto.current.x);
    const y = Math.min(0, e.clientY - gesto.current.y);
    setDesplazamiento({ x, y });
    if (-x > UMBRAL_CANCELAR) descartar();
    else if (-y > UMBRAL_BLOQUEAR) {
      setModo("bloqueado");
      setDesplazamiento({ x: 0, y: 0 });
    }
  };

  const alSoltar = () => {
    if (modoRef.current !== "mantener" || !gesto.current) return;
    gesto.current.soltado = true;
    if (Date.now() - gesto.current.t < CLIC_MS || !grabando) {
      // Clic corto (o aún pidiendo permiso): seguir grabando sin mantener
      setModo("bloqueado");
      setDesplazamiento({ x: 0, y: 0 });
      return;
    }
    enviar();
  };

  if (!soportado) return null;

  const activo = !!modo;
  return (
    <>
      {/* Audio descartado: el micrófono cae en la papelera */}
      {tirando && !activo && (
        <div className="laeai-papelera absolute inset-0 z-10 flex items-center rounded-xl bg-white px-4" aria-hidden="true">
          <span className="laeai-papelera__escena">
            <Mic size={16} className="laeai-papelera__mic" />
            <Papelera />
          </span>
          <span className="laeai-papelera__texto ml-3 text-sm text-gray-500 truncate">{t("laeai.audio.discarded")}</span>
        </div>
      )}
      {/* Barra de grabación sobre la caja de texto */}
      {activo && (
        <div className="absolute inset-0 z-10 flex items-center gap-3 rounded-xl bg-white px-4" role="status" aria-live="polite">
          {/* Hasta que el micrófono graba de verdad: gris y "Preparando…", para no hablar antes de tiempo */}
          <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${grabando ? "bg-red-500 animate-pulse" : "bg-gray-300"}`} />
          <span className={`text-sm font-medium tabular-nums ${grabando ? "text-gray-800" : "text-gray-400"}`}>{formatoTiempo(segundos)}</span>
          {!grabando ? (
            <span className="text-sm text-gray-500 truncate">{t("laeai.audio.preparing")}</span>
          ) : modo === "mantener" ? (
            <span
              className="flex items-center gap-1 text-sm text-gray-500 select-none truncate"
              style={{ transform: `translateX(${desplazamiento.x}px)`, opacity: 1 + desplazamiento.x / (UMBRAL_CANCELAR * 1.5) }}
            >
              <ChevronLeft size={16} /> {t("laeai.audio.slideCancel")}
            </span>
          ) : (
            <span className="text-sm text-gray-500 truncate">{t("laeai.audio.recording")}</span>
          )}
          <div className="ml-auto flex items-center gap-1 pr-10">
            {modo === "bloqueado" && (
              <>
                <button
                  type="button"
                  onClick={descartar}
                  title={t("laeai.audio.cancel")}
                  aria-label={t("laeai.audio.cancel")}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-gray-500 cursor-pointer hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  type="button"
                  onClick={enviar}
                  title={t("laeai.audio.send")}
                  aria-label={t("laeai.audio.send")}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border-none bg-gray-800 text-white cursor-pointer hover:bg-gray-700"
                >
                  <SendHorizontal size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Indicador de "desliza hacia arriba para bloquear" */}
      {modo === "mantener" && (
        <div
          className="absolute right-2 bottom-12 z-20 flex flex-col items-center gap-0.5 rounded-full bg-white px-2 py-2 text-gray-500 shadow-md"
          style={{ transform: `translateY(${desplazamiento.y}px)` }}
          title={t("laeai.audio.lockHint")}
        >
          <Lock size={14} />
          <ChevronUp size={14} />
        </div>
      )}

      {/* Botón del micrófono (en bloqueado se oculta: se envía con el botón de la barra) */}
      {modo !== "bloqueado" && (
        <button
          type="button"
          onPointerEnter={preparar}
          onFocus={preparar}
          onPointerDown={alPresionar}
          onPointerMove={alMover}
          onPointerUp={alSoltar}
          onPointerCancel={descartar}
          onContextMenu={(e) => e.preventDefault()}
          disabled={deshabilitado}
          title={t("laeai.audio.record")}
          aria-label={t("laeai.audio.record")}
          className={`relative z-20 flex items-center justify-center w-8 h-8 rounded-lg border-none transition-colors select-none mb-1 ${
            activo
              ? "bg-red-500 text-white scale-125"
              : deshabilitado
              ? "bg-transparent text-gray-300 cursor-default"
              : "bg-[#111] text-white cursor-pointer hover:bg-black"
          }`}
          style={{ touchAction: "none" }}
        >
          <Mic size={16} />
        </button>
      )}
    </>
  );
}
